import { supabase } from '../supabase';
import { 
  ONBOARDING_CONFIG,
  loadSuggestedGamesByName,
  loadPopularGamesForOnboarding
} from '../../config/onboarding';
import { Game } from '../../types';

/**
 * Interface para armazenar o status de onboarding do usuário
 */
interface OnboardingStatus {
  userId: string;
  completed: boolean;
  completedAt?: string;
}

/**
 * Verifica se o usuário já completou o onboarding
 * @param userId ID do usuário
 * @returns true se o onboarding já foi completado
 */
export async function hasCompletedOnboarding(userId: string): Promise<boolean> {
  try {
    if (!userId) {
      console.log('[ONBOARDING] ID de usuário não fornecido');
      return false;
    }

    // Verificar primeiro o cache na sessionStorage
    const cachedStatus = sessionStorage.getItem(`onboarding_status_${userId}`);
    if (cachedStatus === 'completed') {
      console.log('[ONBOARDING] Status de onboarding recuperado do cache: completado');
      return true;
    }

    console.log(`[ONBOARDING] Verificando status de onboarding para usuário ${userId}`);

    // Verificar se há um registro de onboarding para o usuário
    const { data, error } = await supabase
      .from('user_onboarding')
      .select('completed')
      .eq('user_id', userId)
      .eq('completed', true)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') { // PGRST116 significa "não encontrado"
        console.log('[ONBOARDING] Nenhum registro de onboarding encontrado para o usuário');
        return false;
      }
      console.error('[ONBOARDING] Erro ao verificar status de onboarding:', error);
      return false;
    }

    // Se não houver registro ou completed for false, o usuário não completou
    if (!data || data.completed !== true) {
      console.log('[ONBOARDING] Usuário não completou o onboarding');
      return false;
    }

    console.log('[ONBOARDING] Usuário completou o onboarding');
    // Armazenar em cache para evitar consultas repetidas
    sessionStorage.setItem(`onboarding_status_${userId}`, 'completed');
    return true;
  } catch (error) {
    console.error('[ONBOARDING] Erro ao verificar status de onboarding:', error);
    return false;
  }
}

/**
 * Cria ou atualiza o status de onboarding para o usuário
 * @param userId ID do usuário
 * @returns true se a operação foi bem-sucedida
 */
export async function markOnboardingAsCompleted(userId: string): Promise<boolean> {
  try {
    if (!userId) {
      console.error('[ONBOARDING] ID de usuário inválido');
      return false;
    }

    console.log(`[ONBOARDING] Marcando onboarding como concluído para usuário ${userId}`);

    // Primeiro verifica se já existe um registro completado para este usuário
    const { data: existingData } = await supabase
      .from('user_onboarding')
      .select('id, completed')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    // Se já existe um registro com completed=true, não faz nada
    if (existingData?.completed === true) {
      console.log('[ONBOARDING] Usuário já completou onboarding, atualizando cache');
      // Mesmo que já esteja marcado, atualizar o cache
      sessionStorage.setItem(`onboarding_status_${userId}`, 'completed');
      // Também definir a flag temporária
      sessionStorage.setItem('onboarding_completed', 'true');
      return true;
    }
    
    // Eliminar possíveis registros duplicados antes de prosseguir
    if (existingData) {
      // Buscar todos os registros (exceto o mais recente) para remover
      const { data: duplicates } = await supabase
        .from('user_onboarding')
        .select('id')
        .eq('user_id', userId)
        .neq('id', existingData.id);

      // Se houver duplicatas, remover
      if (duplicates && duplicates.length > 0) {
        const ids = duplicates.map(d => d.id);
        console.log(`[ONBOARDING] Removendo ${ids.length} registros duplicados para o usuário ${userId}`);
        
        await supabase
          .from('user_onboarding')
          .delete()
          .in('id', ids);
      }
      
      // Atualizar o registro existente
      const { error: updateError } = await supabase
        .from('user_onboarding')
        .update({
          completed: true,
          completed_at: new Date().toISOString()
        })
        .eq('id', existingData.id);

      if (updateError) {
        console.error('[ONBOARDING] Erro ao atualizar status de onboarding:', updateError);
        return false;
      }
    } else {
      // Se não existe, insere um novo registro
      const { error: insertError } = await supabase
        .from('user_onboarding')
        .insert({
          user_id: userId,
          completed: true,
          completed_at: new Date().toISOString()
        });

      if (insertError) {
        console.error('[ONBOARDING] Erro ao inserir status de onboarding:', insertError);
        return false;
      }
    }

    // Atualizar o cache após a operação bem-sucedida no banco de dados
    console.log('[ONBOARDING] Status de onboarding atualizado no banco, atualizando cache');
    sessionStorage.setItem(`onboarding_status_${userId}`, 'completed');
    // Também definir a flag temporária
    sessionStorage.setItem('onboarding_completed', 'true');

    return true;
  } catch (error) {
    console.error('[ONBOARDING] Erro ao marcar onboarding como concluído:', error);
    return false;
  }
}

/**
 * Carrega os jogos para o onboarding conforme a configuração
 */
export async function loadOnboardingGames(): Promise<Game[]> {
  try {
    if (ONBOARDING_CONFIG.usePopularGames) {
      return await loadPopularGamesForOnboarding(ONBOARDING_CONFIG.popularGamesCount);
    } else {
      return await loadSuggestedGamesByName(ONBOARDING_CONFIG.suggestedGames);
    }
  } catch (error) {
    console.error('[ONBOARDING] Erro ao carregar jogos para onboarding:', error);
    return [];
  }
}

/**
 * Verifica se o onboarding está habilitado na configuração
 */
export function isOnboardingEnabled(): boolean {
  return ONBOARDING_CONFIG.enabled;
} 